import { Button, FontAwesomeIcon, Stack, Tag, Text } from "@skailer-com/uikit";
import { useTranslation } from "react-i18next";

export interface EmailFlowTagItem {
  id: string;
  delayDays: number;
  errors: string[];
}

export interface EmailFlowTagsProps {
  activeFlowIndex: number;
  initialFlowErrors: string[];
  flows: EmailFlowTagItem[];
  onAdd: () => void;
  onDelete: (index: number) => void;
  onSelect: (index: number) => void;
}

export function EmailFlowTags({
  activeFlowIndex,
  initialFlowErrors,
  flows,
  onAdd,
  onDelete,
  onSelect,
}: EmailFlowTagsProps) {
  const { t } = useTranslation();
  const hasFlowErrors =
    flows.length > 0 &&
    ((activeFlowIndex !== 0 && initialFlowErrors.length > 0) ||
      flows.some((flow, index) => activeFlowIndex !== index + 1 && flow.errors.length > 0));
  const initialFlowHasVisibleErrors =
    flows.length > 0 && activeFlowIndex !== 0 && initialFlowErrors.length > 0;

  return (
    <Stack className={"py-4"} gap={"sm"}>
      <Stack direction={"horizontal"} gap={"sm"} className={"flex-wrap"}>
        <Tag
          className={
            activeFlowIndex === 0
              ? "cursor-pointer bg-brand-dark text-light border-brand-dark"
              : "cursor-pointer"
          }
          variant={initialFlowHasVisibleErrors ? "danger" : "primary"}
          onClick={() => onSelect(0)}
        >
          {t("ats:first_message")}
        </Tag>

        {flows.map((flow, index) => (
          <Tag
            key={flow.id}
            className={
              activeFlowIndex === index + 1
                ? "cursor-pointer gap-(--gap-xs) bg-brand-dark text-light border-brand-dark"
                : "cursor-pointer gap-(--gap-xs)"
            }
            variant={activeFlowIndex !== index + 1 && flow.errors.length > 0 ? "danger" : "primary"}
            onClick={() => onSelect(index + 1)}
          >
            <FontAwesomeIcon icon={"fa-light fa-clock"} />
            {t("ats:flow_after_last_message", { count: flow.delayDays })}
            <span
              role={"button"}
              tabIndex={0}
              aria-label={t("common:delete")}
              className={"cursor-pointer"}
              onClick={(event) => {
                event.stopPropagation();
                onDelete(index);
              }}
            >
              <FontAwesomeIcon icon={"fa-light fa-xmark"} />
            </span>
          </Tag>
        ))}

        {flows.length < 5 && (
          <Button
            variant={"secondary"}
            size={"sm"}
            leftIcon={<FontAwesomeIcon icon={"fa-light fa-plus"} />}
            onClick={onAdd}
          >
            {t("ats:add_follow_up")}
          </Button>
        )}
      </Stack>

      {hasFlowErrors && <Text className={"text-sm text-danger"}>{t("ats:flows_have_errors")}</Text>}

      {flows.length > 0 && (
        <Text className={"text-secondary text-sm"}>{t("ats:follow_ups_description")}</Text>
      )}
    </Stack>
  );
}
